import moment from 'moment'
import converter from 'number-to-words'
import { useState } from 'react'
import { useEffect } from 'react'
import { useRef } from 'react'
import { useSelector } from 'react-redux'

import { selectIsInternalAdmin } from '../../../accessors/index'
import CanadianCheckInstructions from '../../../features/Billing/MakePayment/MakeCheckPayment/CanadianCheckInstructions/index'
import AccountActivatedDialog from '../AccountActivatedDialog/index'
import CheckBg from './CheckBg/index'
import HandWrittenText from './HandWrittenText/index'
import Hint from './Hint/index'
import { hintCopy } from './hintCopy'
import styles from './PayWithCheck.module.scss'
import VoidImage from './void.png'
import WhiteInput from './WhiteInput/index'

const ElectronicCheck = ({ formObject, isVoid, isActivation, isAmountLocked }) => {
  const nameOnCheckRef = useRef()
  const checkNoRef = useRef()

  const [amount, setAmount] = useState(formObject[0].amount)

  const [formData, handleFormChange, setFormData] = formObject

  const [isShowingCanadianCheckHelp, setShowingCanadianCheckHelp] = useState()

  const [isShowingActivatedDialog, setShowingActivatedDialog] = useState()

  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  useEffect(() => {
    setFormData({ PaymentClearedOn: moment().format('YYYY-MM-DD') })
  }, [setFormData])

  useEffect(() => {
    setFormData({ amount: Number(`${amount}`.replace(',', '')) })
  }, [amount, setFormData])

  const {
    bankRoutingNumber,
    accountNumber,
    checkNumber,
    institutionNumber,
    branchNumber,
    usedSavedCheck,
    PaymentClearedOn,
    country,
    holder
  } = formData

  function numberToWords(rawNumber) {
    const number = Number(`${rawNumber}`.replace(',', '')).toFixed(2)

    if (!number || number === 0) {
      return
    }

    const match = `${number}`.replace(',', '').match(/(\d+)(?:\.(\d+))/)

    if (match) {
      // eslint-disable-next-line no-unused-vars
      const [_full, base, decimals] = match
      return `${converter.toWords(base)} and ${decimals}/100`
    } else {
      return converter.toWords(number)
    }
  }

  const [isHintVisible, setHintVisible] = useState()
  const [hintContent, setHintContent] = useState()
  const [hintTargetPosition, setHintTargetPosition] = useState()

  function handleBlurInput(e) {
    setHintVisible(false)

    if (e.target.name === 'bankRoutingNumber' && bankRoutingNumber.length === 9) {
      fetch(`https://www.routingnumbers.info/api/data.json?rn=${bankRoutingNumber}`)
        .then(res => res.json())
        .then(data => {
          if (data.customer_name) {
            setFormData({ bankName: data.customer_name })
          }
        })
    }
  }

  function handleFocusInput(e) {
    setHintVisible(true)
    setHintContent(hintCopy[country === 'CA' ? 'canada' : 'US'][e.target.name])

    const { left, top, right } = e.target.getBoundingClientRect()

    setHintTargetPosition({ left, top, right })
  }

  useEffect(() => {
    if (usedSavedCheck && checkNoRef.current) {
      checkNoRef.current.focus()
    }
  }, [usedSavedCheck])

  function handlePriceInputClick(e) {
    e.target.setSelectionRange(0, e.target.value.length)
  }

  const [highlightBankName, setHighlightBankName] = useState(false)

  useEffect(() => {
    if (usedSavedCheck) {
      setHighlightBankName(true)
    }

    if (country === 'US' && bankRoutingNumber && accountNumber) {
      setHighlightBankName(true)
    }

    if (country === 'CA' && branchNumber && institutionNumber && accountNumber) {
      setHighlightBankName(true)
    }
  }, [
    accountNumber,
    bankRoutingNumber,
    branchNumber,
    institutionNumber,
    country,
    usedSavedCheck
  ])

  return (
    <>
      {isShowingActivatedDialog && <AccountActivatedDialog />}
      {isShowingCanadianCheckHelp && (
        <CanadianCheckInstructions
          handleHide={() => setShowingCanadianCheckHelp(false)}
          isActivation={isActivation}
          handleActivate={() => setShowingActivatedDialog(true)}
        />
      )}
      <p className={styles.description}>
        {isVoid ? (
          <span>A voided check is required to verify your payment method.</span>
        ) : (
          <span>To pay, simply fill in white fields in the check.</span>
        )}
        <br />
        {isInternalAdmin ? (
          <>
            <button
              className={styles.linkButton}
              onClick={() => setFormData({ country: country === 'US' ? 'CA' : 'US' })}
            >
              Click here
            </button>{' '}
            to switch to {country === 'US' ? 'Canadian' : 'US'} check (admin only)
          </>
        ) : (
          <>
            All US checks are eligible for paperless transactions. If you are using a
            Canadian check,{' '}
            <button
              className={styles.linkButton}
              onClick={() => setShowingCanadianCheckHelp(true)}
            >
              click here.
            </button>
          </>
        )}
      </p>
      <div className={styles.wrapper}>
        <CheckBg>
          <div className={styles.inner}>
            {isVoid && <img className={styles.voidStamp} src={VoidImage} alt="Void" />}
            <div className={styles.topRow}>
              <div className={styles.address}>
                <WhiteInput
                  isHandWritten
                  value={holder}
                  onChange={e => setFormData({ holder: e.target.value })}
                  onFocus={handleFocusInput}
                  onBlur={handleBlurInput}
                  ref={nameOnCheckRef}
                  name="nombreOnCheck"
                  autoFill={false}
                  autoComplete="off"
                  disabled={!!usedSavedCheck}
                  autoFocus
                />
              </div>
              {!isVoid && (
                <div className={styles.date}>
                  <div className={styles.dateLabel}>DATE</div>
                  <HandWrittenText isSmall={true}>{PaymentClearedOn}</HandWrittenText>
                </div>
              )}
            </div>
            <div className={styles.middleRow}>
              <div className={styles.payToLabel}>Pay to the order of</div>
              <HandWrittenText width={400}>
                {isVoid ? '' : 'SwayPay Inc.'}
              </HandWrittenText>
              <div className={styles.paymentAmount}>
                <div className={styles.paymentAmountLabel}>USD</div>
                <WhiteInput
                  isHandWritten
                  onChange={e => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                  maxLength="10"
                  onFocus={handlePriceInputClick}
                  onBlur={e =>
                    setAmount(
                      Number(e.target.value).toLocaleString('en-US', {
                        minimumFractionDigits: 2
                      })
                    )
                  }
                  disabled={isVoid || isAmountLocked}
                  value={
                    isVoid
                      ? ''
                      : amount.toLocaleString('en-US', {
                          minimumFractionDigits: 2
                        })
                  }
                />
              </div>
            </div>
            <div className={styles.secondMiddleRow}>
              <HandWrittenText width={470} fontSize={19}>
                {isVoid ? '' : numberToWords(amount)}
              </HandWrittenText>
              <div className={styles.smallPriceLabel}>U.S. Dollars</div>
              <input
                type="text"
                placeholder={highlightBankName ? 'Enter bank name' : null}
                className={styles.bankName}
                value={formData.bankName}
                onChange={e => setFormData({ bankName: e.target.value })}
                tabIndex={highlightBankName ? 0 : 1}
              />
            </div>

            {country === 'CA' ? (
              <div className={styles.lastRow}>
                <WhiteInput
                  name="checkNumber"
                  value={checkNumber}
                  onChange={handleFormChange}
                  maxLength={10}
                  width={120}
                  onFocus={handleFocusInput}
                  onBlur={handleBlurInput}
                />
                <div style={{ width: 40 }}></div>
                <WhiteInput
                  name="branchNumber"
                  value={usedSavedCheck ? '*****' : branchNumber}
                  onChange={handleFormChange}
                  maxLength={5} // max 5 digits
                  width={84}
                  onFocus={handleFocusInput}
                  onBlur={handleBlurInput}
                />
                <WhiteInput
                  name="institutionNumber"
                  value={usedSavedCheck ? '*****' : institutionNumber}
                  onChange={handleFormChange}
                  maxLength={3} // max 3 digits
                  width={60}
                  onFocus={handleFocusInput}
                  onBlur={handleBlurInput}
                />
                <WhiteInput
                  name="accountNumber"
                  value={usedSavedCheck ? `***${usedSavedCheck.last4}` : accountNumber}
                  onChange={handleFormChange}
                  maxLength={7} // max 7
                  width={190}
                  onFocus={handleFocusInput}
                  onBlur={handleBlurInput}
                />
              </div>
            ) : (
              <div className={styles.lastRow}>
                <WhiteInput
                  name="bankRoutingNumber"
                  value={usedSavedCheck ? '*****' : bankRoutingNumber}
                  disabled={!!usedSavedCheck}
                  onChange={handleFormChange}
                  maxLength={10}
                  width={190}
                  onFocus={handleFocusInput}
                  onBlur={handleBlurInput}
                />
                <WhiteInput
                  name="accountNumber"
                  disabled={!!usedSavedCheck}
                  value={usedSavedCheck ? `***${usedSavedCheck.last4}` : accountNumber}
                  onChange={handleFormChange}
                  maxLength={15}
                  width={190}
                  onFocus={handleFocusInput}
                  onBlur={handleBlurInput}
                />
                <WhiteInput
                  name="checkNumber"
                  value={checkNumber}
                  onChange={handleFormChange}
                  maxLength={10}
                  width={130}
                  ref={checkNoRef}
                  onFocus={handleFocusInput}
                  onBlur={handleBlurInput}
                />
              </div>
            )}
            <Hint {...hintContent} {...hintTargetPosition} isActive={isHintVisible} />
          </div>
        </CheckBg>
      </div>
    </>
  )
}

export default ElectronicCheck
